import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Lock, Plus, Trash2, Pencil, LogOut, Upload, X, Image as ImageIcon, GripVertical, FileText, LayoutList, Save, Package, Users, Map, Eye, EyeOff, Check, History } from "lucide-react";
import { api, resolveImg, setAdminToken, clearAdminToken, getAdminToken } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import ContentEditor from "./admin/ContentEditor";
import ProductsManager from "./admin/ProductsManager";
import ClientsManager from "./admin/ClientsManager";
import ReachManager from "./admin/ReachManager";
import JourneyManager from "./admin/JourneyManager";

const EMPTY = { company_name: "", location: "", year: "", area_sqft: "", category: "", description: "", photos: [], visible: true, installation_days: "" };

export default function Admin() {
  const [authed, setAuthed] = useState(false);
  const [password, setPassword] = useState("");
  const [checking, setChecking] = useState(true);
  const [tab, setTab] = useState("projects");

  const [categories, setCategories] = useState([]);
  const [projects, setProjects] = useState([]);
  const [newCat, setNewCat] = useState("");
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const dragIndex = useRef(null);
  const [dragOverId, setDragOverId] = useState(null);

  useEffect(() => {
    (async () => {
      if (getAdminToken()) {
        try {
          await api.get("/admin/verify");
          setAuthed(true);
        } catch {
          clearAdminToken();
        }
      }
      setChecking(false);
    })();
  }, []);

  const loadData = async () => {
    const [cats, projs] = await Promise.all([api.get("/categories"), api.get("/projects", { params: { include_hidden: true } })]);
    setCategories(cats.data);
    setProjects(projs.data);
  };

  useEffect(() => { if (authed) loadData(); }, [authed]);

  const login = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/admin/login", { password });
      setAdminToken(data.token);
      setAuthed(true);
      toast.success("Welcome back.");
    } catch {
      toast.error("Incorrect password.");
    }
  };

  const logout = () => { clearAdminToken(); setAuthed(false); setPassword(""); };

  const addCategory = async (e) => {
    e.preventDefault();
    if (!newCat.trim()) return;
    try {
      await api.post("/categories", { name: newCat.trim() });
      setNewCat("");
      toast.success("Category added.");
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to add category.");
    }
  };

  const deleteCategory = async (id) => {
    await api.delete(`/categories/${id}`);
    toast.success("Category removed.");
    loadData();
  };

  const renameCategory = async (id, newName) => {
    const trimmed = (newName || "").trim();
    if (!trimmed) return;
    try {
      await api.put(`/categories/${id}`, { name: trimmed });
      toast.success("Category renamed.");
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Rename failed.");
    }
  };

  const uploadPhotos = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      const urls = [];
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        urls.push(data.url);
      }
      setForm((f) => ({ ...f, photos: [...f.photos, ...urls] }));
      toast.success(`${urls.length} photo(s) uploaded.`);
    } catch {
      toast.error("Upload failed.");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const removePhoto = (idx) => setForm((f) => ({ ...f, photos: f.photos.filter((_, i) => i !== idx) }));

  const editProject = (p) => {
    setEditingId(p.id);
    setForm({ company_name: p.company_name, location: p.location, year: p.year, area_sqft: p.area_sqft, category: p.category, description: p.description || "", photos: p.photos || [], visible: p.visible !== false, installation_days: p.installation_days || "" });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const toggleVisibility = async (p) => {
    try {
      const newVisible = !(p.visible !== false);
      await api.put(`/projects/${p.id}/visibility`, { visible: newVisible });
      toast.success(newVisible ? "Project now visible." : "Project hidden from site.");
      loadData();
    } catch {
      toast.error("Failed to update visibility.");
    }
  };

  const resetForm = () => { setForm(EMPTY); setEditingId(null); };

  const saveProject = async (e) => {
    e.preventDefault();
    if (!form.company_name || !form.location || !form.year || !form.area_sqft || !form.category) {
      toast.error("Please fill all required fields.");
      return;
    }
    setSaving(true);
    try {
      if (editingId) {
        await api.put(`/projects/${editingId}`, form);
        toast.success("Project updated.");
      } else {
        await api.post("/projects", form);
        toast.success("Project added.");
      }
      resetForm();
      loadData();
    } catch {
      toast.error("Failed to save project.");
    } finally {
      setSaving(false);
    }
  };

  const deleteProject = async (id) => {
    await api.delete(`/projects/${id}`);
    toast.success("Project deleted.");
    loadData();
  };

  // ---- Drag & drop reordering ----
  const onDragStart = (i) => { dragIndex.current = i; };
  const onDragEnter = (i) => {
    const from = dragIndex.current;
    if (from === null || from === i) return;
    setProjects((prev) => {
      const next = [...prev];
      const [moved] = next.splice(from, 1);
      next.splice(i, 0, moved);
      return next;
    });
    dragIndex.current = i;
    setDragOverId(projects[i]?.id ?? null);
  };
  const onDragEnd = async () => {
    dragIndex.current = null;
    setDragOverId(null);
    try {
      await api.put("/projects/reorder", { ids: projects.map((p) => p.id) });
      toast.success("Project order saved.");
    } catch {
      toast.error("Failed to save order.");
      loadData();
    }
  };

  if (checking) return <div className="min-h-screen bg-[#eaf6fc]" />;

  // LOGIN
  if (!authed) {
    return (
      <div className="min-h-screen bg-[#eaf6fc] flex items-center justify-center px-6 relative">
        <form onSubmit={login} data-testid="admin-login-form" className="relative z-10 w-full max-w-md border border-black/10 bg-white p-10">
          <Link to="/" className="inline-block border border-black/10 p-2 mb-8"><img src="/assets/manik-logo.png" alt="Manik" className="h-10" /></Link>
          <div className="flex items-center gap-3 mb-2 text-[#3da8d8]"><Lock size={18} /><span className="font-mono-accent text-xs uppercase tracking-widest">Admin Access</span></div>
          <h1 className="font-display font-bold text-3xl text-[#334155] mb-8">Sign in to manage projects</h1>
          <Label className="font-mono-accent text-[10px] uppercase tracking-widest text-[#52525B]">Password</Label>
          <Input data-testid="admin-password-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="mt-2 rounded-none border-black/15 h-12" placeholder="••••••••" />
          <button type="submit" data-testid="admin-login-btn" className="btn-fill w-full mt-6 border border-[#3da8d8] px-8 py-4 font-mono-accent text-xs uppercase tracking-widest text-[#3da8d8] hover:text-white">Enter Dashboard</button>
        </form>
      </div>
    );
  }

  // DASHBOARD
  return (
    <div className="min-h-screen bg-[#F9FAFB]" data-testid="admin-dashboard">
      <header className="bg-white border-b border-black/10">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 py-5 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="border border-black/10 p-1.5"><img src="/assets/manik-logo.png" alt="Manik" className="h-8" /></div>
            <span className="font-mono-accent text-xs uppercase tracking-widest text-[#52525B]">Admin Dashboard</span>
          </div>
          <div className="flex items-center gap-4">
            <Link to="/" className="font-mono-accent text-xs uppercase tracking-widest text-[#52525B] hover:text-[#3da8d8]">View Site</Link>
            <button onClick={logout} data-testid="admin-logout-btn" className="inline-flex items-center gap-2 font-mono-accent text-xs uppercase tracking-widest text-[#334155] hover:text-[#3da8d8]"><LogOut size={14} /> Logout</button>
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-[1400px] px-6 lg:px-10 pt-8">
        <div className="border-b border-black/10 flex gap-2 overflow-x-auto">
          <button
            onClick={() => setTab("projects")}
            data-testid="admin-tab-projects"
            className={`px-5 py-3 font-mono-accent text-[11px] uppercase tracking-widest inline-flex items-center gap-2 border-b-2 -mb-px transition-colors whitespace-nowrap ${
              tab === "projects" ? "border-[#3da8d8] text-[#3da8d8]" : "border-transparent text-[#52525B] hover:text-[#334155]"
            }`}
          >
            <LayoutList size={13} /> Projects
          </button>
          <button
            onClick={() => setTab("products")}
            data-testid="admin-tab-products"
            className={`px-5 py-3 font-mono-accent text-[11px] uppercase tracking-widest inline-flex items-center gap-2 border-b-2 -mb-px transition-colors whitespace-nowrap ${
              tab === "products" ? "border-[#3da8d8] text-[#3da8d8]" : "border-transparent text-[#52525B] hover:text-[#334155]"
            }`}
          >
            <Package size={13} /> Products
          </button>
          <button
            onClick={() => setTab("clients")}
            data-testid="admin-tab-clients"
            className={`px-5 py-3 font-mono-accent text-[11px] uppercase tracking-widest inline-flex items-center gap-2 border-b-2 -mb-px transition-colors whitespace-nowrap ${
              tab === "clients" ? "border-[#3da8d8] text-[#3da8d8]" : "border-transparent text-[#52525B] hover:text-[#334155]"
            }`}
          >
            <Users size={13} /> Clients
          </button>
          <button
            onClick={() => setTab("reach")}
            data-testid="admin-tab-reach"
            className={`px-5 py-3 font-mono-accent text-[11px] uppercase tracking-widest inline-flex items-center gap-2 border-b-2 -mb-px transition-colors whitespace-nowrap ${
              tab === "reach" ? "border-[#3da8d8] text-[#3da8d8]" : "border-transparent text-[#52525B] hover:text-[#334155]"
            }`}
          >
            <Map size={13} /> Reach
          </button>
          <button
            onClick={() => setTab("journey")}
            data-testid="admin-tab-journey"
            className={`px-5 py-3 font-mono-accent text-[11px] uppercase tracking-widest inline-flex items-center gap-2 border-b-2 -mb-px transition-colors whitespace-nowrap ${
              tab === "journey" ? "border-[#3da8d8] text-[#3da8d8]" : "border-transparent text-[#52525B] hover:text-[#334155]"
            }`}
          >
            <History size={13} /> Journey
          </button>
          <button
            onClick={() => setTab("content")}
            data-testid="admin-tab-content"
            className={`px-5 py-3 font-mono-accent text-[11px] uppercase tracking-widest inline-flex items-center gap-2 border-b-2 -mb-px transition-colors whitespace-nowrap ${
              tab === "content" ? "border-[#3da8d8] text-[#3da8d8]" : "border-transparent text-[#52525B] hover:text-[#334155]"
            }`}
          >
            <FileText size={13} /> Site Content
          </button>
        </div>
      </div>

      {tab === "content" ? (
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 py-10">
          <ContentEditor />
        </div>
      ) : tab === "products" ? (
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 py-10">
          <ProductsManager />
        </div>
      ) : tab === "clients" ? (
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 py-10">
          <ClientsManager />
        </div>
      ) : tab === "reach" ? (
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 py-10">
          <ReachManager />
        </div>
      ) : tab === "journey" ? (
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 py-10">
          <JourneyManager />
        </div>
      ) : (
      <div className="mx-auto max-w-[1400px] px-6 lg:px-10 py-12 grid lg:grid-cols-12 gap-10">
        {/* LEFT */}
        <div className="lg:col-span-5 space-y-10">
          <div className="border border-black/10 bg-white p-8">
            <h2 className="font-display font-semibold text-2xl mb-6 text-[#334155]">{editingId ? "Edit Project" : "Add Project"}</h2>
            <form onSubmit={saveProject} data-testid="admin-project-form" className="space-y-4">
              <div>
                <Label className="font-mono-accent text-[10px] uppercase tracking-widest text-[#52525B]">Company Name *</Label>
                <Input data-testid="admin-company-input" value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} className="mt-1 rounded-none border-black/15" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="font-mono-accent text-[10px] uppercase tracking-widest text-[#52525B]">Location *</Label>
                  <Input data-testid="admin-location-input" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} className="mt-1 rounded-none border-black/15" />
                </div>
                <div>
                  <Label className="font-mono-accent text-[10px] uppercase tracking-widest text-[#52525B]">Year *</Label>
                  <Input data-testid="admin-year-input" value={form.year} onChange={(e) => setForm({ ...form, year: e.target.value })} className="mt-1 rounded-none border-black/15" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="font-mono-accent text-[10px] uppercase tracking-widest text-[#52525B]">Area (sq ft) *</Label>
                  <Input data-testid="admin-area-input" value={form.area_sqft} onChange={(e) => setForm({ ...form, area_sqft: e.target.value })} className="mt-1 rounded-none border-black/15" />
                </div>
                <div>
                  <Label className="font-mono-accent text-[10px] uppercase tracking-widest text-[#52525B]">Category *</Label>
                  <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                    <SelectTrigger data-testid="admin-category-select" className="mt-1 rounded-none border-black/15"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>
                      {categories.map((c) => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label className="font-mono-accent text-[10px] uppercase tracking-widest text-[#52525B]">On site installation days</Label>
                <Input
                  data-testid="admin-installation-days-input"
                  type="number"
                  min="0"
                  placeholder="Leave empty to hide on card"
                  value={form.installation_days}
                  onChange={(e) => setForm({ ...form, installation_days: e.target.value })}
                  className="mt-1 rounded-none border-black/15"
                />
                <p className="mt-1 font-mono-accent text-[10px] uppercase tracking-widest text-[#52525B]/70">
                  Shows as “On site installation in X days” on the project card. Leave blank to hide.
                </p>
              </div>
              <div>
                <Label className="font-mono-accent text-[10px] uppercase tracking-widest text-[#52525B]">Description</Label>
                <Textarea data-testid="admin-description-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1 rounded-none border-black/15" />
              </div>

              <div>
                <Label className="font-mono-accent text-[10px] uppercase tracking-widest text-[#52525B]">Photos</Label>
                <div className="mt-2 grid grid-cols-4 gap-2">
                  {form.photos.map((ph, i) => (
                    <div key={i} className="relative aspect-square border border-black/10 overflow-hidden group">
                      <img src={resolveImg(ph)} alt="" className="w-full h-full object-cover" />
                      <button type="button" onClick={() => removePhoto(i)} data-testid={`admin-remove-photo-${i}`} className="absolute top-1 right-1 bg-[#3da8d8] text-white p-1 opacity-0 group-hover:opacity-100 transition-opacity"><X size={12} /></button>
                    </div>
                  ))}
                  <label className={`aspect-square border border-dashed border-black/25 flex flex-col items-center justify-center cursor-pointer hover:border-[#3da8d8] transition-colors ${uploading ? "opacity-50" : ""}`} data-testid="admin-upload-label">
                    <Upload size={18} className="text-[#52525B]" />
                    <span className="font-mono-accent text-[8px] uppercase mt-1 text-[#52525B]">{uploading ? "…" : "Upload"}</span>
                    <input type="file" accept="image/*" multiple className="hidden" onChange={uploadPhotos} data-testid="admin-photo-input" />
                  </label>
                </div>
              </div>

              <div className="flex gap-3 pt-2">
                <button type="submit" disabled={saving} data-testid="admin-save-project-btn" className="btn-fill flex-1 border border-[#3da8d8] px-6 py-3 font-mono-accent text-xs uppercase tracking-widest text-[#3da8d8] hover:text-white disabled:opacity-50">
                  {saving ? "Saving…" : editingId ? "Update Project" : "Add Project"}
                </button>
                {editingId && <button type="button" onClick={resetForm} data-testid="admin-cancel-edit-btn" className="border border-black/20 px-6 py-3 font-mono-accent text-xs uppercase tracking-widest text-[#334155]">Cancel</button>}
              </div>
            </form>
          </div>

          <div className="border border-black/10 bg-white p-8">
            <h2 className="font-display font-semibold text-2xl mb-6 text-[#334155]">Categories</h2>
            <form onSubmit={addCategory} className="flex gap-2 mb-5">
              <Input data-testid="admin-new-category-input" value={newCat} onChange={(e) => setNewCat(e.target.value)} className="rounded-none border-black/15" placeholder="New category name" />
              <button type="submit" data-testid="admin-add-category-btn" className="bg-[#3da8d8] text-white px-4 flex items-center hover:bg-[#3a82ba] transition-colors"><Plus size={18} /></button>
            </form>
            <ul className="space-y-2">
              {categories.map((c) => (
                <CategoryRow key={c.id} cat={c} onRename={renameCategory} onDelete={deleteCategory} />
              ))}
            </ul>
          </div>
        </div>

        {/* RIGHT: projects list */}
        <div className="lg:col-span-7">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-display font-semibold text-2xl text-[#334155]">Projects <span className="text-[#52525B] text-lg">({projects.length})</span></h2>
          </div>
          <p className="font-mono-accent text-[10px] uppercase tracking-widest text-[#52525B] mb-6 flex items-center gap-2">
            <GripVertical size={13} className="text-[#3da8d8]" /> Drag rows to set the order shown on the Projects page
          </p>
          <div className="space-y-3">
            {projects.map((p, i) => (
              <div
                key={p.id}
                data-testid={`admin-project-row-${p.id}`}
                draggable
                onDragStart={() => onDragStart(i)}
                onDragEnter={() => onDragEnter(i)}
                onDragOver={(e) => e.preventDefault()}
                onDragEnd={onDragEnd}
                className={`flex items-center gap-3 border bg-white p-3 transition-colors ${dragOverId === p.id ? "border-[#3da8d8]" : "border-black/10"} ${p.visible === false ? "opacity-60" : ""}`}
              >
                <span data-testid={`admin-drag-handle-${p.id}`} className="cursor-grab active:cursor-grabbing text-[#94A3B8] hover:text-[#3da8d8] shrink-0"><GripVertical size={18} /></span>
                <div className="w-20 h-16 bg-[#eaf6fc] shrink-0 overflow-hidden flex items-center justify-center relative">
                  {p.photos[0] ? <img src={resolveImg(p.photos[0])} alt="" className="w-full h-full object-cover" /> : <ImageIcon size={20} className="text-black/20" />}
                  {p.visible === false && (
                    <span data-testid={`admin-project-hidden-badge-${p.id}`} className="absolute inset-0 bg-black/60 flex items-center justify-center">
                      <EyeOff size={16} className="text-white" />
                    </span>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="font-display font-semibold text-lg truncate text-[#334155]">{p.company_name}</p>
                    {p.visible === false && (
                      <span data-testid={`admin-project-hidden-pill-${p.id}`} className="font-mono-accent text-[9px] uppercase tracking-widest bg-red-100 text-red-700 px-2 py-0.5">Hidden</span>
                    )}
                  </div>
                  <p className="text-xs text-[#52525B] truncate">{p.location} · {p.year} · {p.area_sqft} sq ft</p>
                  <span className="font-mono-accent text-[9px] uppercase tracking-widest text-[#3da8d8]">{p.category}</span>
                </div>
                <div className="flex gap-2 shrink-0">
                  <button
                    onClick={() => toggleVisibility(p)}
                    data-testid={`admin-toggle-visibility-${p.id}`}
                    title={p.visible === false ? "Show on site" : "Hide from site"}
                    className={`w-9 h-9 border flex items-center justify-center transition-colors ${p.visible === false ? "border-red-300 text-red-600 hover:border-red-500 hover:bg-red-50" : "border-black/15 text-[#334155] hover:border-[#78be27] hover:text-[#78be27]"}`}
                  >
                    {p.visible === false ? <EyeOff size={15} /> : <Eye size={15} />}
                  </button>
                  <button onClick={() => editProject(p)} data-testid={`admin-edit-${p.id}`} className="w-9 h-9 border border-black/15 flex items-center justify-center text-[#334155] hover:border-[#3da8d8] hover:text-[#3da8d8] transition-colors"><Pencil size={15} /></button>
                  <button onClick={() => deleteProject(p.id)} data-testid={`admin-delete-${p.id}`} className="w-9 h-9 border border-black/15 flex items-center justify-center text-[#334155] hover:border-red-500 hover:text-red-600 transition-colors"><Trash2 size={15} /></button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      )}
    </div>
  );
}

function CategoryRow({ cat, onRename, onDelete }) {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(cat.name);

  useEffect(() => setName(cat.name), [cat.name]);

  const save = () => {
    const trimmed = name.trim();
    if (!trimmed || trimmed === cat.name) { setEditing(false); setName(cat.name); return; }
    onRename(cat.id, trimmed);
    setEditing(false);
  };

  return (
    <li data-testid={`admin-category-row-${cat.id}`} className="flex items-center justify-between border border-black/10 px-4 py-2.5 gap-2">
      {editing ? (
        <>
          <Input
            autoFocus
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") save(); if (e.key === "Escape") { setEditing(false); setName(cat.name); } }}
            className="flex-1 h-8 rounded-none border-black/15 text-sm"
            data-testid={`admin-category-input-${cat.id}`}
          />
          <button onClick={save} data-testid={`admin-category-save-${cat.id}`} className="text-[#78be27] hover:text-[#6aa81f]"><Check size={16} /></button>
          <button onClick={() => { setEditing(false); setName(cat.name); }} data-testid={`admin-category-cancel-${cat.id}`} className="text-[#52525B] hover:text-[#334155]"><X size={16} /></button>
        </>
      ) : (
        <>
          <span className="text-sm text-[#334155] flex-1 truncate">{cat.name}</span>
          <button onClick={() => setEditing(true)} data-testid={`admin-rename-category-${cat.id}`} title="Rename" className="text-[#52525B] hover:text-[#3da8d8] transition-colors"><Pencil size={14} /></button>
          <button onClick={() => onDelete(cat.id)} data-testid={`admin-delete-category-${cat.id}`} className="text-[#52525B] hover:text-red-600 transition-colors"><Trash2 size={15} /></button>
        </>
      )}
    </li>
  );
}
